import { SortDirection } from 'utils/tableHelpers';

export enum InvoiceConfigDueDateAlertUnit {
    HOURS = 'HOURS',
    DAYS = 'DAYS',
}

export type InvoiceConfigDueDateAlert = {
    unit?: InvoiceConfigDueDateAlertUnit | null;
    amount: number | null;
};

export type InvoiceConfigBankAccount = {
    paymentMethod: 'BANK_TRANSFER';
    iban: string;
    bic: string;
    bankName: string;
};

export type InvoiceConfigEmailTemplate = {
    subject: string;
    body: string;
    footer: string;
};

export type UpdateInvoiceConfigGeneralSettingsPayload = {
    invoiceDueDays: number | null;
    dueDateAlert: InvoiceConfigDueDateAlert | null;
    additionalCharge: {
        id: string | null;
    };
};

export type UpdateInvoiceConfigInvoiceTemplatePayload = {
    standardTerms: string | null;
    bankAccount: InvoiceConfigBankAccount[];
    paymentLink: string | null;
    paymentNotes: string | null;
};

export type UpdateInvoiceConfigEmailTempaltePayload = {
    subject?: string | null;
    body?: string | null;
    footer?: string | null;
};

export type InvoiceConfig = {
    invoiceDueDays: number | null;
    dueDateAlert: InvoiceConfigDueDateAlert | null;
    additionalCharge: AdditionalCharge | null;
    standardTerms: string | null;
    emailTemplate: InvoiceConfigEmailTemplate | null;
    bankAccount: InvoiceConfigBankAccount[];
    paymentLink: string | null; // PayPal link
    paymentNotes: string | null;
};

export type TaxCode = {
    code: string;
    ratePercent: number | null;
    label: string;
    kind: 'zero' | 'standard' | 'reduced' | 'exempt' | 'non_vatable';
};

export const IRELAND_DEFAULT_TAX_CODES: TaxCode[] = [
    { code: 'T0', label: 'T0 - Zero-rated (0%)', ratePercent: 0, kind: 'zero' },
    { code: 'T1', label: 'T1 - Standard-rated (23%)', ratePercent: 23, kind: 'standard' },
    { code: 'T3', label: 'T3 - Reduced rate (13.5%)', ratePercent: 13.5, kind: 'reduced' },
    { code: 'T2', label: 'T2 - Exempt', ratePercent: null, kind: 'exempt' },
    { code: 'T5', label: 'T5 - Exempt import of goods from suppliers in EC', ratePercent: null, kind: 'exempt' },
    { code: 'T9', label: 'T9 - Non-Vatable (No VAT)', ratePercent: null, kind: 'non_vatable' },
];

export const getIrelandTaxCodeLabel = (code: string): string => {
    return IRELAND_DEFAULT_TAX_CODES.find((tax) => tax.code === code)?.label ?? '-';
};

export const getIrelandTaxCodePercents = (code: string): number => {
    return IRELAND_DEFAULT_TAX_CODES.find((tax) => tax.code === code)?.ratePercent ?? 0;
};

export type ListAdditionalChargesParams = Partial<{
    sortDirection: SortDirection;
    sort: ListAdditionalChargesSortParams;
    page: number;
    size: number;
    query: string;
    archived: boolean;
    customerId: string;
}>;

export type CreateChargePayload = {
    productCode: string;
    description: string;
    nominalCode: string;
    taxCode: string;
    amount: number;
    percentage?: number | null;
};

export type AdditionalCharge = {
    id: string;
    productCode: string;
    description: string;
    nominalCode: string;
    taxCode: string;
    amount: number;
    percentage?: number;
    tenantId: string;
    customerId: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    archived: true;
};

export type UpdateCustomerAdditionalChargePayload = {
    productCode: string;
    description: string;
    nominalCode: string;
    taxCode: string;
    amount: number;
    percentage?: number;
};

export type ListAdditionalChargesSortParams =
    | 'ID'
    | 'PRODUCT_CODE'
    | 'DESCRIPTION'
    | 'NOMINAL_CODE'
    | 'TAX_CODE'
    | 'AMOUNT'
    | 'PERCENTAGE';
